import L from "leaflet";

const START_COLOR = "#16a34a";
const FINISH_COLOR = "#dc2626";

function ensureCourseMarkerStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("course-marker-styles")) return;

  const style = document.createElement("style");
  style.id = "course-marker-styles";
  style.textContent = `
    .leaflet-div-icon.course-marker {
      background: none;
      border: none;
    }
  `;
  document.head.appendChild(style);
}

function iconsForLabel(label) {
  if (label === "Start/Finish") {
    return '<i class="fas fa-flag"></i><i class="fas fa-flag-checkered"></i>';
  }
  if (label === "Finish") {
    return '<i class="fas fa-flag-checkered"></i>';
  }
  return '<i class="fas fa-flag"></i>';
}

function teardropHtml(label, color, size = 32) {
  const height = Math.round((size * 42) / 32);
  const circleY = Math.round((size * 15) / 32);
  const iconSize =
    label === "Start/Finish"
      ? Math.max(7, Math.round((size * 7) / 32))
      : Math.max(8, Math.round((size * 10) / 32));

  return `
    <div style="position:relative;width:${size}px;height:${height}px;pointer-events:none;">
      <svg width="${size}" height="${height}" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 26 16 26S32 27.5 32 16C32 7.163 24.837 0 16 0z"
          fill="${color}" stroke="#fff" stroke-width="1.5"/>
        <circle cx="16" cy="15" r="7" fill="#fff"/>
      </svg>
      <div style="
        position:absolute;
        left:50%;
        top:${circleY}px;
        transform:translate(-50%,-50%);
        color:${color};
        font-size:${iconSize}px;
        line-height:1;
        display:flex;
        gap:1px;
        align-items:center;
      ">${iconsForLabel(label)}</div>
    </div>
  `;
}

function sameCoursePoint(start, finish) {
  return (
    start &&
    finish &&
    start.lat === finish.lat &&
    start.lng === finish.lng
  );
}

/**
 * Teardrop pin with tip at the lat/lng (accurate placement).
 */
export function courseMarkerIcon(label, color) {
  ensureCourseMarkerStyles();
  return L.divIcon({
    className: "course-marker",
    html: teardropHtml(label, color),
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42],
  });
}

export function addCourseMarkers(map, { start, finish }, layerGroupRef) {
  if (layerGroupRef.current) {
    map.removeLayer(layerGroupRef.current);
    layerGroupRef.current = null;
  }

  const layers = [];
  const samePoint = sameCoursePoint(start, finish);

  if (start) {
    const label = samePoint ? "Start/Finish" : "Start";
    layers.push(
      L.marker([start.lat, start.lng], {
        icon: courseMarkerIcon(label, START_COLOR),
        interactive: false,
      })
    );
  }

  if (finish && !samePoint) {
    layers.push(
      L.marker([finish.lat, finish.lng], {
        icon: courseMarkerIcon("Finish", FINISH_COLOR),
        interactive: false,
      })
    );
  }

  if (layers.length === 0) return [];

  const group = L.layerGroup(layers).addTo(map);
  layerGroupRef.current = group;
  return layers.map((m) => m.getLatLng());
}

function legendItems({ start, finish }) {
  const samePoint = sameCoursePoint(start, finish);
  const items = [];

  if (start && samePoint) {
    items.push({ label: "Start/Finish", color: START_COLOR });
    return items;
  }
  if (start) items.push({ label: "Start", color: START_COLOR });
  if (finish) items.push({ label: "Finish", color: FINISH_COLOR });
  return items;
}

function legendHtml(items) {
  const rows = items
    .map(
      (item) => `
        <div style="display:flex;align-items:center;gap:6px;">
          ${teardropHtml(item.label, item.color, 18)}
          <span>${item.label}</span>
        </div>
      `
    )
    .join("");

  return `
    <div style="
      background:#fff;
      padding:8px 10px;
      border-radius:8px;
      box-shadow:0 1px 4px rgba(0,0,0,0.3);
      font:12px/1.3 system-ui,sans-serif;
      color:#111;
      display:flex;
      flex-direction:column;
      gap:4px;
    ">${rows}</div>
  `;
}

export function addCourseLegend(map, { start, finish }, legendRef, options = {}) {
  if (legendRef.current) {
    map.removeControl(legendRef.current);
    legendRef.current = null;
  }

  const items = options.items || legendItems({ start, finish });
  if (items.length === 0) return;

  const Legend = L.Control.extend({
    onAdd() {
      const div = L.DomUtil.create("div", "course-legend");
      div.innerHTML = legendHtml(items);
      L.DomEvent.disableClickPropagation(div);
      return div;
    },
  });

  const control = new Legend({ position: "bottomleft" });
  map.addControl(control);
  legendRef.current = control;
}
